import { ArrowRightIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { cn } from '../../../lib/utils.ts';

export type AttentionPoint = {
    label: string;
    details: string[];
};

export type AttentionSummary = {
    points: AttentionPoint[];
    onReview: () => void;
};

const DETAILS_ID = 'attention-details';
const BANNER_CLASS = 'flex h-9 min-w-0 items-center gap-2 rounded-md border px-3 text-[12.5px]';

export function AttentionSentence({ points, onReview }: AttentionSummary) {
    const count = points.length;
    if (count === 0) {
        return null;
    }
    const sentence = count === 1 ? '1 punt vraagt aandacht' : `${count} punten vragen aandacht`;
    const [firstPoint] = points;
    const remaining = count - 1;
    return (
        <div
            role="status"
            aria-live="polite"
            className={cn(BANNER_CLASS, 'border-amber-200 bg-amber-50 text-amber-900')}
        >
            <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
            <span className="group relative min-w-0 flex-1">
                <span
                    tabIndex={0}
                    aria-describedby={DETAILS_ID}
                    className="block cursor-default truncate rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                >
                    <span className="font-semibold">{sentence}</span>
                    <span>
                        {' · '}
                        {firstPoint.label}
                        {remaining > 0 && ` en ${remaining} meer`}
                    </span>
                </span>
                <span
                    id={DETAILS_ID}
                    role="tooltip"
                    className="invisible absolute left-0 top-full z-50 mt-1.5 w-max max-w-md rounded-md border border-gray-200 bg-white px-3 py-2 text-[12.5px] font-normal text-gray-700 opacity-0 shadow-lg transition-opacity group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100"
                >
                    <ul className="list-disc space-y-1 pl-4">
                        {points.map((point, index) => (
                            <li key={`${point.label}-${index}`}>
                                {point.label}
                                {point.details.length > 0 && (
                                    <ul className="mt-0.5 list-[circle] space-y-0.5 pl-4 text-gray-500">
                                        {point.details.map((detail) => (
                                            <li key={detail}>{detail}</li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>
                </span>
            </span>
            <button
                type="button"
                onClick={onReview}
                className="ml-auto inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap rounded font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
            >
                Ga naar punt
                <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
        </div>
    );
}

export function CompleteBanner() {
    return (
        <div role="status" aria-live="polite" className={cn(BANNER_CLASS, 'border-green-200 bg-green-50 text-green-800')}>
            <CheckCircleIcon className="h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
            <span className="font-semibold">Alles ingevuld</span>
        </div>
    );
}

export function CheckingBanner() {
    return (
        <div role="status" aria-live="polite" className={cn(BANNER_CLASS, 'border-gray-200 bg-gray-50 text-gray-600')}>
            <span className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-gray-300 border-t-primary" aria-hidden="true" />
            <span>Document wordt gecontroleerd…</span>
        </div>
    );
}
